import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import {
  ChevronLeft, ChevronRight, Users, Clock, Lock, Download, Plane
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MonthlyCalendar = ({ isManagerOrAdmin, onNavigateToWeek }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/monthly-overview?year=${year}&month=${month}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverview(res.data);
    } catch (err) {
      console.error('Failed to load monthly overview:', err);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const navigate = (dir) => {
    const d = new Date(currentDate);
    d.setMonth(d.getMonth() + dir);
    setCurrentDate(d);
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/export/monthly-csv?year=${year}&month=${month}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `schedule_${currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported successfully');
    } catch (err) {
      toast.error('Failed to export CSV');
    }
  };

  // Build calendar grid
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const startPad = (firstDay.getDay() + 6) % 7; // Monday-first
  const totalDays = lastDay.getDate();

  const daysMap = {};
  if (overview?.days) {
    overview.days.forEach(d => { daysMap[d.date] = d; });
  }
  const lockedWeeks = new Set(overview?.locked_weeks || []);
  const holidays = new Set(overview?.holidays || []);

  const isWeekLocked = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    const day = d.getDay();
    const monday = new Date(d);
    monday.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
    return lockedWeeks.has(monday.toISOString().split('T')[0]);
  };

  const today = new Date().toISOString().split('T')[0];

  const cells = [];
  // Day labels
  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ day: d, date: dateStr, data: daysMap[dateStr] || { total_shifts: 0, regular: 0, on_call: 0, pto: 0, employees: [] } });
  }

  return (
    <div className="space-y-4" data-testid="monthly-calendar">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)} data-testid="monthly-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-lg font-bold text-gray-900 min-w-[200px] text-center">
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h2>
          <Button variant="outline" size="icon" onClick={() => navigate(1)} data-testid="monthly-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>Today</Button>
        </div>
        <Button variant="outline" size="sm" onClick={handleExportCSV} data-testid="export-monthly-csv">
          <Download className="h-4 w-4 mr-1" />
          Export CSV
        </Button>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span> Regular</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span> On-Call</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-violet-500"></span> PTO</span>
        <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> Locked</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-400"></span> Holiday</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          {/* Day Headers */}
          <div className="grid grid-cols-7 bg-gray-50 border-b">
            {dayLabels.map(label => (
              <div key={label} className="py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {label}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7">
            {cells.map((cell, idx) => {
              if (!cell) {
                return <div key={`pad-${idx}`} className="min-h-[100px] bg-gray-50/50 border-b border-r"></div>;
              }
              const { day, date: dateStr, data } = cell;
              const isToday = dateStr === today;
              const isHoliday = holidays.has(dateStr);
              const locked = isWeekLocked(dateStr);
              const isWeekend = new Date(dateStr + 'T12:00:00').getDay() === 0 || new Date(dateStr + 'T12:00:00').getDay() === 6;

              return (
                <div
                  key={dateStr}
                  className={`min-h-[100px] border-b border-r p-1.5 cursor-pointer transition-colors hover:bg-blue-50/50 ${isWeekend ? 'bg-gray-50/30' : ''} ${isHoliday ? 'bg-red-50/50' : ''}`}
                  onClick={() => setSelectedDay(cell)}
                  data-testid={`monthly-day-${dateStr}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-semibold rounded-full ${isToday ? 'bg-blue-600 text-white' : 'text-gray-700'}`}>
                      {day}
                    </span>
                    <div className="flex items-center gap-0.5">
                      {locked && <Lock className="h-3 w-3 text-orange-400" />}
                      {isHoliday && <span className="text-xs">H</span>}
                    </div>
                  </div>

                  {data.total_shifts > 0 && (
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <Users className="h-3 w-3" />
                        <span className="font-medium">{data.total_shifts}</span>
                      </div>
                      <div className="flex gap-0.5">
                        {data.regular > 0 && (
                          <span className="inline-block px-1 py-0.5 text-[10px] rounded bg-emerald-100 text-emerald-700 font-medium">
                            {data.regular}R
                          </span>
                        )}
                        {data.on_call > 0 && (
                          <span className="inline-block px-1 py-0.5 text-[10px] rounded bg-amber-100 text-amber-700 font-medium">
                            {data.on_call}OC
                          </span>
                        )}
                        {data.pto > 0 && (
                          <span className="inline-block px-1 py-0.5 text-[10px] rounded bg-violet-100 text-violet-700 font-medium">
                            {data.pto}P
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Day Detail Dialog */}
      <Dialog open={!!selectedDay} onOpenChange={() => setSelectedDay(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedDay && new Date(selectedDay.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </DialogTitle>
            <DialogDescription>Schedule details for this day</DialogDescription>
          </DialogHeader>
          {selectedDay && (
            <div className="space-y-3">
              <div className="flex gap-3 text-sm">
                <Badge variant="outline" className="bg-emerald-50">{selectedDay.data.regular} Regular</Badge>
                <Badge variant="outline" className="bg-amber-50">{selectedDay.data.on_call} On-Call</Badge>
                <Badge variant="outline" className="bg-violet-50">{selectedDay.data.pto} PTO</Badge>
              </div>
              {selectedDay.data.employees?.length > 0 ? (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {selectedDay.data.employees.map((emp, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 border text-sm">
                      <div>
                        <span className="font-medium">{emp.name}</span>
                        <Badge variant="outline" className="ml-2 text-xs">{emp.role}</Badge>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500">
                        <Clock className="h-3 w-3" />
                        <span className="text-xs">{emp.start_time} - {emp.end_time}</span>
                        {emp.shift_type === 'on_call' && <Badge className="bg-amber-100 text-amber-700 text-xs">OC</Badge>}
                        {emp.shift_type === 'pto' && <Badge className="bg-violet-100 text-violet-700 text-xs">PTO</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm text-center py-4">No shifts scheduled</p>
              )}
              {onNavigateToWeek && (
                <Button variant="outline" className="w-full" onClick={() => {
                  onNavigateToWeek(selectedDay.date);
                  setSelectedDay(null);
                }} data-testid="navigate-to-week-btn">
                  View in Weekly Schedule
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MonthlyCalendar;
